import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Header } from 'components/common/header/Header';
import { useAlert } from 'contexts/AlertContext';
import { ScheduleRepository } from 'repository/scheduleRepository';
import './ScheduleInput.css';

interface ScheduleItem {
    id: string;
    date: string;
    startTime: string;
    endTime: string;
}

export const ScheduleInput = () => {
    const navigate = useNavigate();
    const { meetingId } = useParams();
    const { showAlert } = useAlert();

    const [selectedDate, setSelectedDate] = useState('');
    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');
    const [scheduleList, setScheduleList] = useState<ScheduleItem[]>([]);

    const [showDatePicker, setShowDatePicker] = useState(false);
    const [showStartTimePicker, setShowStartTimePicker] = useState(false);

    // 달력 상태
    const [currentMonth, setCurrentMonth] = useState(new Date());

    // 시간 피커 상태
    const [tempPeriod, setTempPeriod] = useState<'오전' | '오후'>('오후');
    const [tempHour, setTempHour] = useState(6);
    const [tempMinute, setTempMinute] = useState(0);

    const hours = Array.from({ length: 12 }, (_, i) => i + 1);
    const minutes = Array.from({ length: 12 }, (_, i) => i * 5); // 5분 단위

    const hourScrollRef = useRef<HTMLDivElement>(null);
    const minuteScrollRef = useRef<HTMLDivElement>(null);
    const hourScrollTimer = useRef<NodeJS.Timeout | null>(null);
    const minuteScrollTimer = useRef<NodeJS.Timeout | null>(null);

    const MAX_SCHEDULES = 3;

    // 컴포넌트 마운트 시 기존 제안 불러오기
    useEffect(() => {
        const loadExistingProposals = async () => {
            if (!meetingId) return;

            try {
                const response = await ScheduleRepository.onGetMyProposals(meetingId);
                const proposals = response.data?.data;

                if (proposals && proposals.length > 0) {
                    console.log('기존 제안 로드:', proposals);

                    // ScheduleProposal을 ScheduleItem 형식으로 변환
                    const existingSchedules = proposals.map((proposal: any) => {
                        const startDate = new Date(proposal.startDatetime);
                        const endDate = new Date(proposal.endDatetime);

                        return {
                            id: proposal.proposalId,
                            date: startDate.toISOString().split('T')[0], // YYYY-MM-DD
                            startTime: `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`,
                            endTime: `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`
                        };
                    });

                    setScheduleList(existingSchedules);
                }
            } catch (error) {
                console.error('기존 제안 로드 실패:', error);
            }
        };

        loadExistingProposals();
    }, [meetingId]);

    // 달력 생성 (Step2 방식)
    const renderCalendar = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth() + 1;
        const daysInMonth = new Date(year, month, 0).getDate();
        const firstDayOfMonth = new Date(year, month - 1, 1).getDay();

        const days = [];
        const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

        // Week day headers
        const headers = weekDays.map((day, idx) => (
            <div key={`header-${day}`} className={`calendar-header-cell ${idx === 0 ? 'sunday' : idx === 6 ? 'saturday' : ''}`}>
                {day}
            </div>
        ));

        // Empty cells for days before month starts
        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
        }

        // Days of month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month - 1, day);
            const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isSelected = selectedDate === dateStr;
            const isToday = day === today.getDate() &&
                           month - 1 === today.getMonth() &&
                           year === today.getFullYear();
            const isPast = date < today;
            const isSunday = date.getDay() === 0;
            const isSaturday = date.getDay() === 6;

            days.push(
                <button
                    key={day}
                    className={`calendar-day
                        ${isSelected ? 'selected' : ''}
                        ${isToday ? 'today' : ''}
                        ${isPast ? 'past' : ''}
                        ${isSunday ? 'sunday' : isSaturday ? 'saturday' : ''}`}
                    onClick={() => handleDateSelect(date)}
                    disabled={isPast}
                >
                    {day}
                </button>
            );
        }

        return (
            <div className="calendar">
                <div className="calendar-header">
                    <button className="calendar-nav" onClick={handlePrevMonth}>←</button>
                    <span className="calendar-month">{year}년 {month}월</span>
                    <button className="calendar-nav" onClick={handleNextMonth}>→</button>
                </div>
                <div className="calendar-grid">
                    {headers}
                    {days}
                </div>
            </div>
        );
    };

    // 날짜 포맷팅
    const formatDateDisplay = (dateString: string) => {
        if (!dateString) return '날짜를 선택해주세요';
        const date = new Date(dateString);
        const days = ['일', '월', '화', '수', '목', '금', '토'];
        return `${date.getMonth() + 1}월 ${date.getDate()}일 (${days[date.getDay()]})`;
    };

    // 시간 포맷팅
    const formatTimeDisplay = (timeString: string) => {
        if (!timeString) return '--:--';
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours);
        const period = hour >= 12 ? '오후' : '오전';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${period} ${displayHour}:${minutes}`;
    };

    // 날짜 선택
    const handleDateSelect = (date: Date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (date < today) return; // 과거 날짜 선택 불가

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        setSelectedDate(`${year}-${month}-${day}`);
        setShowDatePicker(false);
    };

    // 이전 월
    const handlePrevMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
    };

    // 다음 월
    const handleNextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
    };

    // 시간 피커 열기 (시작 시간만)
    const openTimePicker = (type: 'start' | 'end') => {
        // 종료 시간은 자동 설정되므로 시작 시간만 편집 가능
        if (type === 'end') return;

        // 기존 값이 있으면 파싱
        if (startTime) {
            const [hours, minutes] = startTime.split(':');
            const hour = parseInt(hours);
            setTempPeriod(hour >= 12 ? '오후' : '오전');
            setTempHour(hour === 0 ? 12 : hour > 12 ? hour - 12 : hour);
            setTempMinute(parseInt(minutes));
        } else {
            setTempPeriod('오후');
            setTempHour(6);
            setTempMinute(0);
        }

        setShowStartTimePicker(true);

        // 다음 프레임에서 스크롤 위치 설정
        setTimeout(() => {
            scrollToCenter(tempHour, 'hour');
            scrollToCenter(tempMinute, 'minute');
        }, 100);
    };

    // 시간 선택 완료 (시작 시간만)
    const handleTimeConfirm = (type: 'start' | 'end') => {
        // 종료 시간은 편집할 수 없음
        if (type === 'end') return;

        let hour24 = tempHour;
        if (tempPeriod === '오후' && tempHour !== 12) {
            hour24 = tempHour + 12;
        } else if (tempPeriod === '오전' && tempHour === 12) {
            hour24 = 0;
        }

        const timeString = `${String(hour24).padStart(2, '0')}:${String(tempMinute).padStart(2, '0')}`;
        setStartTime(timeString);

        // 종료 시간을 시작 시간 + 3시간으로 자동 설정
        let endHour24 = hour24 + 3;
        if (endHour24 >= 24) {
            endHour24 = endHour24 - 24;
        }
        const endTimeString = `${String(endHour24).padStart(2, '0')}:${String(tempMinute).padStart(2, '0')}`;
        setEndTime(endTimeString);

        setShowStartTimePicker(false);
    };

    // 스크롤 위치를 중앙으로 이동
    const scrollToCenter = (value: number, type: 'hour' | 'minute') => {
        const element = document.getElementById(`${type}-${value}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    // 가장 가까운 중앙 아이템 찾기
    const findClosestItem = (scrollContainer: HTMLDivElement, items: number[], type: 'hour' | 'minute') => {
        const containerRect = scrollContainer.getBoundingClientRect();
        const containerCenter = containerRect.top + containerRect.height / 2;

        let closestItem = items[0];
        let minDistance = Infinity;

        items.forEach(item => {
            const element = document.getElementById(`${type}-${item}`);
            if (element) {
                const itemRect = element.getBoundingClientRect();
                const itemCenter = itemRect.top + itemRect.height / 2;
                const distance = Math.abs(itemCenter - containerCenter);

                if (distance < minDistance) {
                    minDistance = distance;
                    closestItem = item;
                }
            }
        });

        return closestItem;
    };

    // 시간 스크롤 핸들러
    const handleHourScroll = () => {
        if (hourScrollTimer.current) {
            clearTimeout(hourScrollTimer.current);
        }

        hourScrollTimer.current = setTimeout(() => {
            if (hourScrollRef.current) {
                const closestHour = findClosestItem(hourScrollRef.current, hours, 'hour');
                setTempHour(closestHour);
                scrollToCenter(closestHour, 'hour');
            }
        }, 150);
    };

    // 분 스크롤 핸들러
    const handleMinuteScroll = () => {
        if (minuteScrollTimer.current) {
            clearTimeout(minuteScrollTimer.current);
        }

        minuteScrollTimer.current = setTimeout(() => {
            if (minuteScrollRef.current) {
                const closestMinute = findClosestItem(minuteScrollRef.current, minutes, 'minute');
                setTempMinute(closestMinute);
                scrollToCenter(closestMinute, 'minute');
            }
        }, 150);
    };

    // 일정 추가
    const handleAddSchedule = () => {
        if (!selectedDate) {
            showAlert({ message: '날짜를 선택해주세요.' });
            return;
        }
        if (!startTime) {
            showAlert({ message: '시작 시간을 선택해주세요.' });
            return;
        }
        if (!endTime) {
            showAlert({ message: '종료 시간을 선택해주세요.' });
            return;
        }
        if (startTime >= endTime) {
            showAlert({ message: '종료 시간은 시작 시간보다 늦어야 합니다.' });
            return;
        }
        if (scheduleList.length >= MAX_SCHEDULES) {
            showAlert({ message: `최대 ${MAX_SCHEDULES}개까지만 등록할 수 있습니다.` });
            return;
        }

        const isDuplicate = scheduleList.some(
            schedule => schedule.date === selectedDate &&
                       schedule.startTime === startTime &&
                       schedule.endTime === endTime
        );
        if (isDuplicate) {
            showAlert({ message: '이미 등록된 일정입니다.' });
            return;
        }

        const newSchedule: ScheduleItem = {
            id: Date.now().toString(),
            date: selectedDate,
            startTime,
            endTime
        };

        setScheduleList([...scheduleList, newSchedule]);
        setStartTime('');
        setEndTime('');
    };

    // 일정 삭제
    const handleDeleteSchedule = (id: string) => {
        setScheduleList(scheduleList.filter(schedule => schedule.id !== id));
    };

    // 완료
    const handleSubmit = async () => {
        if (scheduleList.length === 0) {
            showAlert({ message: '최소 1개 이상의 일정을 등록해주세요.' });
            return;
        }

        if (!meetingId) {
            showAlert({ message: '모임 정보를 찾을 수 없습니다.' });
            return;
        }

        try {
            // ScheduleItem을 API 형식으로 변환
            const proposals = scheduleList.map(schedule => {
                // date: YYYY-MM-DD, startTime: HH:mm, endTime: HH:mm
                const startDatetime = `${schedule.date}T${schedule.startTime}:00`;
                const endDatetime = `${schedule.date}T${schedule.endTime}:00`;

                return {
                    startDatetime,
                    endDatetime
                };
            });

            console.log('제출할 일정 제안:', { meetingId, proposals });

            const response = await ScheduleRepository.onPostCreateProposals({
                meetingId,
                proposals
            });

            console.log('일정 제안 등록 응답:', response);

            // API 응답이 성공이면 (에러가 발생하지 않으면) 성공으로 간주
            await showAlert({
                message: response.data?.message || `${scheduleList.length}개의 일정이 등록되었습니다.`
            });
            navigate(`/schedule/main/${meetingId}`);
        } catch (error) {
            console.error('일정 제안 등록 실패:', error);
            showAlert({ message: '일정 등록 중 오류가 발생했습니다.' });
        }
    };

    // 날짜 포맷 (MM월 DD일)
    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return `${date.getMonth() + 1}월 ${date.getDate()}일`;
    };

    // 요일
    const getDayOfWeek = (dateString: string) => {
        const date = new Date(dateString);
        const days = ['일', '월', '화', '수', '목', '금', '토'];
        return days[date.getDay()];
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 시간 프리셋
    const timeSlots = ['오후 6:00', '오후 6:30', '오후 7:00', '오후 7:30'];

    return (
        <div className="schedule-input-wrapper">
            <Header title="내 일정 입력" isSubPage />

            <div className="schedule-input-content">
                {/* 안내 문구 */}
                <div className="schedule-input-description">
                    <div className="description-icon">📅</div>
                    <div className="description-text">
                        <div className="description-title">가능한 일정을 등록해주세요</div>
                        <div className="description-subtitle">
                            최대 {MAX_SCHEDULES}개까지 등록 가능합니다
                            {scheduleList.length > 0 && ' • 완료 시 기존 제안이 교체됩니다'}
                        </div>
                    </div>
                </div>

                {/* 일정 입력 폼 */}
                <div className="schedule-input-form">
                    {/* 날짜 선택 */}
                    <div className="form-section">
                        <label className="form-label">날짜</label>
                        <div
                            className="native-picker-button"
                            onClick={() => setShowDatePicker(true)}
                        >
                            <div className="picker-icon">📆</div>
                            <div className="picker-value">
                                {formatDateDisplay(selectedDate)}
                            </div>
                        </div>
                    </div>

                    {/* 시간 선택 (시작 시간만, 종료는 자동 +3시간) */}
                    <div className="form-section">
                        <label className="form-label">시간 선택</label>
                        <div className="time-chips">
                            {timeSlots.map(time => (
                                <button
                                    key={time}
                                    className={`time-chip ${formatTimeDisplay(startTime) === time ? 'selected' : ''}`}
                                    onClick={() => {
                                        // "오후 6:00" -> "18:00" 변환
                                        const match = time.match(/(오전|오후)\s*(\d+):(\d+)/);
                                        if (match) {
                                            let hour = parseInt(match[2]);
                                            const minute = match[3];
                                            if (match[1] === '오후' && hour !== 12) hour += 12;
                                            if (match[1] === '오전' && hour === 12) hour = 0;
                                            const startTimeString = `${String(hour).padStart(2, '0')}:${minute}`;
                                            setStartTime(startTimeString);

                                            // 종료 시간 자동 설정 (+3시간)
                                            let endHour = hour + 3;
                                            if (endHour >= 24) endHour = endHour - 24;
                                            setEndTime(`${String(endHour).padStart(2, '0')}:${minute}`);
                                        }
                                    }}
                                >
                                    {time}
                                </button>
                            ))}
                            <button
                                className="time-chip direct"
                                onClick={() => openTimePicker('start')}
                            >
                                ⏰ 직접 입력
                            </button>
                        </div>
                        {/* 선택된 시간 표시 */}
                        {startTime && (
                            <div className="selected-custom-time">
                                <div className="custom-time-label">선택한 시간</div>
                                <div className="custom-time-value">
                                    {formatTimeDisplay(startTime)}
                                </div>
                            </div>
                        )}
                    </div>

                    <button
                        className="add-schedule-button"
                        onClick={handleAddSchedule}
                        disabled={scheduleList.length >= MAX_SCHEDULES}
                    >
                        <span className="button-icon">➕</span>
                        일정 추가 ({scheduleList.length}/{MAX_SCHEDULES})
                    </button>
                </div>

                {/* 등록된 일정 목록 */}
                {scheduleList.length > 0 && (
                    <div className="schedule-list-section">
                        <div className="schedule-list-header">
                            <span className="list-title">✨ 등록된 일정</span>
                            <span className="list-count">{scheduleList.length}개</span>
                        </div>
                        <div className="schedule-list">
                            {scheduleList.map((schedule) => (
                                <div key={schedule.id} className="schedule-item">
                                    <div className="schedule-item-icon">📅</div>
                                    <div className="schedule-item-info">
                                        <div className="schedule-item-date">
                                            {formatDate(schedule.date)} ({getDayOfWeek(schedule.date)})
                                        </div>
                                        <div className="schedule-item-time">
                                            {formatTimeDisplay(schedule.startTime)}
                                        </div>
                                    </div>
                                    <button
                                        className="schedule-item-delete"
                                        onClick={() => handleDeleteSchedule(schedule.id)}
                                    >
                                        삭제
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* 하단 완료 버튼 */}
            <div className="schedule-input-footer">
                <button
                    className="submit-button"
                    onClick={handleSubmit}
                    disabled={scheduleList.length === 0}
                >
                    완료
                </button>
            </div>

            {/* 날짜 피커 모달 (캘린더) */}
            {showDatePicker && (
                <div className="picker-modal-overlay" onClick={() => setShowDatePicker(false)}>
                    <div className="picker-modal-sheet calendar-sheet" onClick={(e) => e.stopPropagation()}>
                        <div className="picker-modal-content">
                            {renderCalendar()}
                        </div>
                    </div>
                </div>
            )}

            {/* 시작 시간 피커 모달 */}
            {showStartTimePicker && (
                <div className="picker-modal-overlay" onClick={() => setShowStartTimePicker(false)}>
                    <div className="picker-modal-sheet time-sheet" onClick={(e) => e.stopPropagation()}>
                        <div className="picker-modal-header">
                            <h3>시작 시간</h3>
                            <button className="picker-close" onClick={() => setShowStartTimePicker(false)}>✕</button>
                        </div>
                        <div className="picker-modal-content">
                            {/* 오전/오후 선택 */}
                            <div className="period-toggle">
                                <button
                                    className={`period-button ${tempPeriod === '오전' ? 'active' : ''}`}
                                    onClick={() => setTempPeriod('오전')}
                                >
                                    오전
                                </button>
                                <button
                                    className={`period-button ${tempPeriod === '오후' ? 'active' : ''}`}
                                    onClick={() => setTempPeriod('오후')}
                                >
                                    오후
                                </button>
                            </div>

                            {/* 시간 피커 휠 */}
                            <div className="time-picker-container">
                                <div className="time-picker-overlay-top"></div>
                                <div className="time-picker-selected-indicator"></div>
                                <div className="time-picker-overlay-bottom"></div>

                                <div className="time-picker-wheels">
                                    {/* 시간 피커 */}
                                    <div className="picker-column">
                                        <div
                                            className="picker-scroll"
                                            ref={hourScrollRef}
                                            onScroll={handleHourScroll}
                                        >
                                            {hours.map((h) => (
                                                <div
                                                    key={h}
                                                    id={`hour-${h}`}
                                                    className={`picker-item ${tempHour === h ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        setTempHour(h);
                                                        scrollToCenter(h, 'hour');
                                                    }}
                                                >
                                                    {h}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="picker-separator">:</div>

                                    {/* 분 피커 */}
                                    <div className="picker-column">
                                        <div
                                            className="picker-scroll"
                                            ref={minuteScrollRef}
                                            onScroll={handleMinuteScroll}
                                        >
                                            {minutes.map((m) => (
                                                <div
                                                    key={m}
                                                    id={`minute-${m}`}
                                                    className={`picker-item ${tempMinute === m ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        setTempMinute(m);
                                                        scrollToCenter(m, 'minute');
                                                    }}
                                                >
                                                    {m.toString().padStart(2, '0')}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* 선택된 시간 표시 */}
                            <div className="selected-time-display">
                                <span className="selected-time-text">
                                    {tempPeriod} {tempHour}:{tempMinute.toString().padStart(2, '0')}
                                </span>
                            </div>

                            <button className="time-confirm-button" onClick={() => handleTimeConfirm('start')}>
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};
